import React, { useCallback, useEffect, useRef, useState } from 'react';

import { loadSession, saveSession, clearSession } from '../api';
import { EMPTY_DRAWS, withDraw } from '../draws';
import { EMPTY_SCOREBOARD, withScoreboard } from '../scoreboard';
import { EMPTY_DISPLAY, withDisplay } from '../display';
import { EMPTY_STOPWATCH, withStopwatch } from '../stopwatch';
import useEmotes from '../useEmotes';
import Register from './Register';
import Chat from './chat/Chat';
import Games from './Games';
import Scoreboard from './Scoreboard';
import Stopwatch from './Stopwatch';
import AdminPanel from './admin/AdminPanel';

import './App.css';

const TABS = [
  { id: 'chat', label: 'Chat' },
  { id: 'games', label: 'Sorteos' },
];

// What the bar says over each piece of the app
const TITLES = {
  chat: 'Chat del evento',
  games: 'Sorteos',
  scoreboard: 'Marcador',
  stopwatch: 'Cronómetro',
};

const STATUS_LABELS = {
  connected: 'En vivo',
  connecting: 'Conectando…',
  disconnected: 'Desconectado',
};

// The pages of this web: everything on one screen at /, each piece alone at an address of
// its own (for a screen of the event that shows only that), and the admin panel at /admin.
// CloudFront serves index.html for any path, so one that is no page (say /admin/scoreboard,
// which is a route of the API, not of the web) would show the chat under an address of its
// own: it goes to the page it hangs from instead
const ADMIN_PAGE = '/admin';
const ALONE_PAGES = {
  '/chat': 'chat',
  '/marcador': 'scoreboard',
  '/cronometro': 'stopwatch',
  '/random': 'games', // both jackpots: the stage shows the last one launched
};
const currentPage = () => {
  const path = window.location.pathname.replace(/\/+$/, '').toLowerCase();
  if (path === '' || path === ADMIN_PAGE || ALONE_PAGES[path]) return path;
  const page = path.startsWith(`${ADMIN_PAGE}/`) ? ADMIN_PAGE : '';
  window.history.replaceState(null, '', page || '/');
  return page;
};

const App = () => {
  const [page] = useState(currentPage);
  const [session, setSession] = useState(loadSession);
  const [tab, setTab] = useState('chat');
  const [draws, setDraws] = useState(EMPTY_DRAWS);
  const [scoreboard, setScoreboard] = useState(EMPTY_SCOREBOARD);
  const [display, setDisplay] = useState(EMPTY_DISPLAY);
  const [stopwatch, setStopwatch] = useState(EMPTY_STOPWATCH);
  const gamesShown = useRef(true); // for handleDraw, which the chat keeps from its first render
  const [connection, setConnection] = useState({ status: 'connecting', reconnect: null });
  const emotes = useEmotes();

  const handleRegistered = (newSession) => {
    saveSession(newSession);
    setSession(newSession);
  };

  const handleSessionExpired = () => {
    clearSession();
    setSession(null);
  };

  // A live draw (either jackpot) pulls everyone to the stage tab (a piece alone stays as it
  // is), unless the panel hid the draws: then there is no tab to go to, now or when they come back
  const handleDraw = (draw) => {
    setDraws((current) => withDraw(current, draw));
    if (draw.live && gamesShown.current) setTab('games');
  };

  // The stopwatch of the stage, which the panel starts, pauses and resets
  const handleStopwatch = (incoming) => setStopwatch((current) => withStopwatch(current, incoming));

  // The panel hides the scoreboard, the stopwatch or the draws while the event is not using them
  const handleDisplay = (incoming) => setDisplay((current) => withDisplay(current, incoming));

  // Whoever was looking at the draws when they are hidden goes back to the chat, and stays
  // there when they come back
  useEffect(() => {
    gamesShown.current = display.games;
    if (!display.games) setTab('chat');
  }, [display.games]);

  // The scoreboard of the team games is on screen with both tabs, so nobody is pulled anywhere
  const handleScoreboard = (incoming) => setScoreboard((current) => withScoreboard(current, incoming));

  // The chat owns the connection to the room; the bar on top says how it is going
  const handleStatus = useCallback((status, reconnect) => setConnection({ status, reconnect }), []);

  if (page === ADMIN_PAGE) {
    return <AdminPanel emotes={emotes} />;
  }

  if (!session) {
    return <Register onRegistered={handleRegistered} />;
  }

  const { status, reconnect } = connection;
  const alone = ALONE_PAGES[page]; // nothing at /, where the tabs choose what is on screen
  // What the panel hides, it hides from the page everyone uses. The address of a piece alone
  // always shows it: whoever opens /marcador on a screen of the event asked for the scoreboard
  const tabs = alone || display.games ? TABS : [];
  const view = alone || (display.games ? tab : 'chat');
  const board = alone ? view === 'scoreboard' : display.scoreboard;
  const watch = alone ? view === 'stopwatch' : display.stopwatch;
  // The boards over the content: the scoreboard, the stopwatch, or both side by side
  const boards = board || watch;
  const classes = [
    `app app--${view}`,
    alone && 'app--alone',
    boards && !alone && 'app--board',
    board && watch && 'app--pair',
  ];

  return (
    <div className={classes.filter(Boolean).join(' ')}>
      {/* One bar for both tabs: two rows on a phone, a single one on a wide screen (App.css).
          A piece alone has no tabs to go anywhere else */}
      <header className='bar'>
        <h1 className='bar__title'>{TITLES[view]}</h1>
        {!alone && tabs.length > 0 && (
          <nav className='tabs'>
            {tabs.map(({ id, label }) => (
              <button
                key={id}
                type='button'
                className={`tab${tab === id ? ' tab--active' : ''}`}
                onClick={() => setTab(id)}
              >
                {label}
              </button>
            ))}
          </nav>
        )}
        {status === 'disconnected' ? (
          <button type='button' className='status status--disconnected' onClick={reconnect}>
            Reconectar
          </button>
        ) : (
          <span className={`status status--${status}`}>{STATUS_LABELS[status]}</span>
        )}
      </header>

      {/* A band over the content on a phone; with room across, it floats over the chat; alone,
          it takes the screen (App.css) */}
      {boards && (
        <aside className='app__scoreboard'>
          {board && <Scoreboard scoreboard={scoreboard} />}
          {watch && <Stopwatch stopwatch={stopwatch} />}
        </aside>
      )}

      <main className='app__content' hidden={view === 'scoreboard' || view === 'stopwatch'}>
        {/* Both stay mounted whatever is on screen: the chat keeps its connection, which
            brings the draws and the points too, and the stage keeps its draw */}
        <Chat
          session={session}
          onSessionExpired={handleSessionExpired}
          onDraw={handleDraw}
          onScoreboard={handleScoreboard}
          onDisplay={handleDisplay}
          onStopwatch={handleStopwatch}
          onStatus={handleStatus}
          emotes={emotes}
          hidden={view !== 'chat'}
        />
        <Games draws={draws} participant={session.participant} hidden={view !== 'games'} />
      </main>
    </div>
  );
};

export default App;
